import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { OBJLoader } from "three/addons/loaders/OBJLoader.js";
import { MTLLoader } from "three/addons/loaders/MTLLoader.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import { FBXLoader } from "three/addons/loaders/FBXLoader.js";
import { PLYLoader } from "three/addons/loaders/PLYLoader.js";
import { TDSLoader } from "three/addons/loaders/TDSLoader.js";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import { GUI } from "three/addons/libs/lil-gui.module.min.js";
import CursorNode from "./CursorNode";
import PainterApp from "./ScenePainter";
import { Dialog, Transition } from "@headlessui/react";
import { useTheme } from "../../ThemeContext";
import { editFile } from "../../api/api";

const ThreeScene = ({ modelUrl, open, onClose, extension, fileName, fileMeta }) => {
  const mountRef = useRef(null);
  const { theme } = useTheme();
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [viewerNotice, setViewerNotice] = useState("");

  const [lightParam, setLightParam] = useState({
    pitch: -0.53,
    yaw: -2.2,
  });

  useEffect(() => {
    if (open) return;
    setSaveError("");
    setSaveSuccess("");
    setSaveLoading(false);
    setViewerNotice("");
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let scene = null;
    let camera = null;
    let renderer = null;
    let controls = null;
    let dlight = null;
    let ambientLight = null;
    let painterApp = null;
    let modelGroup = null;
    let gui = null;

    let resizeHandler = null;

    const init = async () => {
      try {
        const container = mountRef.current;
        if (!container) throw new Error("Mount ref not found");

        // ─── Renderer ────────────────────────────────────────
        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.setClearColor(0xcccccc);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        const width = container.clientWidth || 1;
        const height = container.clientHeight || 1;

        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(renderer.domElement);

        // ─── Scene & Camera ──────────────────────────────────
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        camera.position.set(-0.8, 0.5, 1.8);
        sceneRef.current = scene;
        cameraRef.current = camera;

        // ─── Controls ────────────────────────────────────────
        controls = new OrbitControls(camera, renderer.domElement);
        controls.target.set(0, 0.5, 0);
        controls.enableDamping = true;
        controls.maxPolarAngle = Math.PI;
        controlsRef.current = controls;

        // ─── Lighting ────────────────────────────────────────
        const lightRig = new THREE.Object3D();
        dlight = new THREE.DirectionalLight(0xffffff, 0.8);
        dlight.position.set(0, 50, 0);
        dlight.castShadow = true;
        dlight.shadow.mapSize.set(1024, 1024);

        ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        dlight.target.position.set(0, 0.5, 0);
        lightRig.add(ambientLight, dlight, dlight.target);
        scene.add(lightRig);

        const updateLight = () => {
          const { pitch, yaw } = lightParam;
          const lpitch = Math.sin(pitch);
          dlight.position.set(
            Math.sin(yaw) * lpitch,
            Math.cos(pitch),
            Math.cos(yaw) * lpitch
          );
          dlight.position.multiplyScalar(1.5);
          dlight.lookAt(dlight.target.position);
        };
        updateLight();

        // ─── GUI ─────────────────────────────────────────────
        gui = new GUI({ width: 200, container: container });
        gui
          .add(lightParam, "pitch", -Math.PI, 0)
          .name("Light Pitch")
          .onChange(updateLight);
        gui
          .add(lightParam, "yaw", -Math.PI, Math.PI)
          .name("Light Yaw")
          .onChange(updateLight);
        gui
          .add({ wireframe: false }, "wireframe")
          .name("Wireframe")
          .onChange((val) => {
            scene.traverse((child) => {
              if (child.isMesh && child.material) {
                child.material.wireframe = val;
              }
            });
          });

        // ─── Plain Background ─────────────────────────────────
        scene.background = new THREE.Color(0x2a2a2a);

        // ─── Load Model ──────────────────────────────────────
        let url = "";

        // Helper function to fetch file with authentication
        const fetchWithAuth = async (fileUrl, requestOptions = {}) => {
          try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const token = user?.auth_token || localStorage.getItem('access_token');

            const headers = {
              Accept: '*/*',
              ...(requestOptions.headers || {}),
            };

            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(fileUrl, {
              ...requestOptions,
              headers,
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
            }

            return response;
          } catch (fetchErr) {
            console.error('Fetch error:', fetchErr);
            throw fetchErr;
          }
        };

        const fetchBlobWithAuth = async (fileUrl, requestOptions = {}) => {
          const response = await fetchWithAuth(fileUrl, requestOptions);
          return response.blob();
        };

        const loadStlWithLoader = (fileUrl) => {
          return new Promise((resolve, reject) => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const token = user?.auth_token || localStorage.getItem('access_token');
            const loader = new STLLoader();

            if (token && loader.setRequestHeader) {
              loader.setRequestHeader({ Authorization: `Bearer ${token}` });
            }

            loader.load(
              fileUrl,
              (geometry) => resolve(geometry),
              undefined,
              (error) => reject(error)
            );
          });
        };

        // Handle URL construction - prefer string URL, otherwise construct from file object
        if (typeof modelUrl === "string") {
          url = modelUrl;
        } else if (modelUrl && typeof modelUrl === 'object') {
          // Construct URL using backend API endpoint
          const baseURL = process.env.REACT_APP_BASE_URL || '/api/v1';
          const folder = modelUrl?.folderPath || modelUrl?.path || '';
          const name = modelUrl?.orignalFileName || modelUrl?.fileName;
          const fileId = modelUrl?._id;
          const projectId = modelUrl?.projectId;
          
          if (fileId) {
            // Use fileId if available (most reliable)
            url = `${baseURL}/filesysytem/serveFile?fileId=${fileId}`;
          } else if (projectId && name) {
            // Construct URL with projectId, folderPath, and fileName
            url = `${baseURL}/filesysytem/serveFile?projectId=${projectId}&folderPath=${encodeURIComponent(folder)}&fileName=${encodeURIComponent(name)}`;
          } else {
            // Fallback to direct path (less reliable)
            const base = process.env.REACT_APP_IMAGEURL || '/';
            url = `${base}/upload_documents/${folder}/${name}`.replace(/\/+/g, "/");
          }
        } else {
          // Completely invalid modelUrl
          console.error('Invalid modelUrl:', modelUrl);
          throw new Error('No valid model URL or file information provided');
        }
        
        console.log('MonkeyPaint - Final URL:', url);

        let ext = (extension || "").toLowerCase().replace(/^\./, "");
        if (!ext && url) ext = url.split(".").pop().toLowerCase();
        setViewerNotice("");

        if (["gltf", "glb"].includes(ext)) {
          try {
            const loader = new GLTFLoader();
            // Fetch with authentication headers
            const blob = await fetchBlobWithAuth(url);
            const gltf = await loader.parseAsync(await blob.arrayBuffer());
            modelGroup = gltf.scene;
          } catch (err) {
            console.error("GLTF/GLB loading error:", err);
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0x00ff00, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        } else if (ext === "obj") {
          try {
            const objLoader = new OBJLoader();
            // const mtlUrl = url.replace(/\.obj$/i, ".mtl");

            // try {
            //   const mtlLoader = new MTLLoader();
            //   // Fetch MTL with authentication
            //   const mtlBlob = await fetchWithAuth(mtlUrl);
            //   const materials = mtlLoader.parse(await mtlBlob.text());
            //   materials.preload();
            //   objLoader.setMaterials(materials);
            // } catch {
            //   console.warn("MTL not found → default material");
            // }

            // Fetch OBJ with authentication
            const objBlob = await fetchBlobWithAuth(url);
            modelGroup = objLoader.parse(await objBlob.text());

            modelGroup.traverse((child) => {
              if (
                child.isMesh &&
                (!child.material || Array.isArray(child.material))
              ) {
                child.material = new THREE.MeshStandardMaterial({
                  color: 0xaaaaaa,
                  metalness: 0.1,
                  roughness: 0.8,
                });
              }
            });
          } catch (err) {
            console.error("OBJ loading error:", err);
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0x0000ff, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        } else if (ext === "stl") {
          try {
            console.log('Loading STL file from:', url);

            // Reference-style loading first (same pattern as provided sample).
            let geometry;
            try {
              geometry = await loadStlWithLoader(url);
            } catch (loaderErr) {
              console.warn('STLLoader.load failed, trying auth fetch+parse fallback', loaderErr);
              const blob = await fetchBlobWithAuth(url);
              const arrayBuffer = await blob.arrayBuffer();
              geometry = new STLLoader().parse(arrayBuffer);
            }
            
            // Validate geometry after loading
            if (!geometry || !geometry.attributes.position) {
              throw new Error("Invalid STL geometry - no position data");
            }
            
            // Check for valid vertex count
            const vertexCount = geometry.attributes.position.count;
            if (vertexCount === 0 || isNaN(vertexCount)) {
              throw new Error("Invalid STL geometry - empty or corrupted");
            }
            
            modelGroup = new THREE.Mesh(
              geometry,
              new THREE.MeshStandardMaterial({
                color: 0xcccccc,
                metalness: 0.2,
                roughness: 0.6,
              })
            );
          } catch (stlErr) {
            console.error("STL loading error:", stlErr);
            // Create placeholder for failed STL
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ 
              color: 0xff6600,
              wireframe: true 
            });
            modelGroup = new THREE.Mesh(geometry, material);
            setViewerNotice("Failed to load STL model. Check console for details.");
          }
        } else if (ext === "fbx") {
          try {
            const loader = new FBXLoader();
            // Fetch with authentication
            const blob = await fetchBlobWithAuth(url);
            modelGroup = loader.parse(await blob.text(), '');
          } catch (err) {
            console.error("FBX loading error:", err);
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xff00ff, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        } else if (ext === "3ds") {
          try {
            const loader = new TDSLoader();
            // Fetch with authentication
            const blob = await fetchBlobWithAuth(url);
            modelGroup = loader.parse(await blob.arrayBuffer());
          } catch (err) {
            console.error("3DS loading error:", err);
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xffff00, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        } else if (ext === "ply") {
          try {
            const loader = new PLYLoader();
            // Fetch with authentication
            const blob = await fetchBlobWithAuth(url);
            const geometry = loader.parse(await blob.text());
            modelGroup = new THREE.Mesh(
              geometry,
              new THREE.MeshStandardMaterial({
                color: 0xcccccc,
                metalness: 0.2,
                roughness: 0.6,
              })
            );
          } catch (err) {
            console.error("PLY loading error:", err);
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0x00ffff, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
                } else if (["ifc", "rvt", "dwg", "bim"].includes(ext)) {
          // For IFC files, try to load directly using web-ifc-three
          // For RVT/DWG/BIM, try to load a server-converted GLB/GLTF first
          if (ext === "ifc") {
            try {
              console.log('Loading IFC file directly from:', url);
              // Dynamic import to avoid webpack issues
              const { IFCLoader } = await import('web-ifc-three');
              const loader = new IFCLoader();
              
              // Fetch with auth
              const blob = await fetchBlobWithAuth(url);
              const arrayBuffer = await blob.arrayBuffer();
              
              modelGroup = await new Promise((resolve, reject) => {
                loader.load(
                  URL.createObjectURL(new Blob([arrayBuffer], { type: 'application/x-ifc' })),
                  (ifcModel) => {
                    // Apply material to IFC meshes
                    ifcModel.traverse((child) => {
                      if (child.isMesh) {
                        child.material = new THREE.MeshStandardMaterial({
                          color: 0xaaaaaa,
                          metalness: 0.1,
                          roughness: 0.8,
                        });
                      }
                    });
                    resolve(ifcModel);
                  },
                  undefined,
                  reject
                );
              });
              setViewerNotice("✅ IFC file loaded directly! Paint mode enabled.");
            } catch (ifcErr) {
              console.error('IFC direct loading failed:', ifcErr);
              // Fall back to converted model or placeholder
            }
          }
          
          // If IFC didn't load directly, try converted GLTF for any BIM format
          if (!modelGroup) {
            try {
              console.log(`Attempting to load converted GLTF/GLB for .${ext} via backend.`);
              const derivedName = fileMeta?.orignalFileName || fileMeta?.fileName || fileMeta?.name || (url.split('?')[0].split('/').pop() || '');
              const convEndpoint = `/api/v1/bim/converted/${encodeURIComponent(derivedName)}`;
              const resp = derivedName ? await fetchWithAuth(convEndpoint, { method: 'GET' }) : null;
              if (resp && resp.ok) {
                const contentType = resp.headers.get('content-type') || '';
                const blob = await resp.blob();
                if (contentType.includes('model/gltf+json') || contentType.includes('application/json') || contentType.includes('model/gltf-binary') || derivedName.toLowerCase().endsWith('.glb') || derivedName.toLowerCase().endsWith('.gltf')) {
                  const loader = new GLTFLoader();
                  try {
                    const arrayBuffer = await blob.arrayBuffer();
                    const gltf = await loader.parseAsync(arrayBuffer, '');
                    modelGroup = gltf.scene || new THREE.Group();
                    setViewerNotice(`Loaded converted model: ${derivedName}`);
                  } catch (parseErr) {
                    console.warn('GLTF parse failed, will show placeholder:', parseErr);
                  }
                }
              } else {
                console.log('No converted model available for', derivedName);
              }
            } catch (err) {
              console.warn('Error fetching converted model, falling back to placeholder:', err);
            }
          }
          
          // If modelGroup wasn't set, show a lightweight placeholder
          if (!modelGroup) {
            console.warn(`Direct loader unavailable for .${ext}. Opening interactive placeholder in paint mode.`);
            setViewerNotice(
              `.${ext.toUpperCase()} file loaded. Using placeholder for paint workflow. ` +
              `For best results, use converted GLB/GLTF format.`
            );
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xff8844, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        } else if (ext === "evtf") {
          // Try loading EVTF with GLTF loader as fallback - might work if file is actually GLTF format
          try {
            const loader = new GLTFLoader();
            const blob = await fetchBlobWithAuth(url);
            const gltf = await loader.parseAsync(await blob.arrayBuffer());
            modelGroup = gltf.scene;
            setViewerNotice("✅ .EVTF file loaded successfully!");
          } catch (err) {
            console.error("EVTF loading error:", err);
            setViewerNotice(
              `📐 .EVTF file could not be loaded. ` +
              `Supported 3D formats: OBJ, GLTF/GLB, STL, FBX, 3DS, PLY. ` +
              `Please convert your .EVTF file to a web-compatible format to view it. ` +
              `You can still use the painting tools on this placeholder.`
            );
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xff8844, wireframe: true });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        } else {
          // Try GLTF loader as fallback for unknown formats
          try {
            const loader = new GLTFLoader();
            // Fetch with authentication
            const blob = await fetchBlobWithAuth(url);
            const gltf = await loader.parseAsync(await blob.arrayBuffer());
            modelGroup = gltf.scene;
          } catch (fallbackErr) {
            console.warn(`Unsupported 3D format: .${ext || "unknown"}, tried fallback loading`, fallbackErr);
            // Create a placeholder cube for unsupported formats
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xff0000 });
            modelGroup = new THREE.Mesh(geometry, material);
          }
        }

        if (!modelGroup || !modelGroup.isGroup && !modelGroup.isMesh) {
          console.warn("ScenePainter: modelGroup is null or invalid");
          return;
        }

        // ─── Clean up geometry (remove NaN values) ─────────────────
        const cleanGeometry = (mesh) => {
          if (!mesh || !mesh.geometry) return mesh;
          
          const geometry = mesh.geometry;
          const positionAttr = geometry.attributes.position;
          
          if (!positionAttr) return mesh;
          
          const positions = positionAttr.array;
          let hasNaN = false;
          
          // Check for NaN values
          for (let i = 0; i < positions.length; i++) {
            if (isNaN(positions[i]) || !isFinite(positions[i])) {
              hasNaN = true;
              positions[i] = 0;
            }
          }
          
          if (hasNaN) {
            console.warn("Geometry contained NaN values, cleaned up");
            positionAttr.needsUpdate = true;
          }
          
          // Compute bounding sphere to fix NaN radius issue
          try {
            geometry.computeBoundingSphere();
            if (isNaN(geometry.boundingSphere.radius)) {
              geometry.boundingSphere.radius = 1;
              geometry.boundingSphere.center.set(0, 0, 0);
            }
          } catch (e) {
            console.warn("Could not compute bounding sphere:", e);
            geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1);
          }
          
          return mesh;
        };

        // Apply cleanup to all meshes in the model
        modelGroup.traverse((child) => {
          if (child.isMesh) {
            cleanGeometry(child);
          }
        });

        // ─── Center & scale ─────────────────────────────────
        const box = new THREE.Box3().setFromObject(modelGroup);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        if (!box.isEmpty()) {
          modelGroup.position.sub(center);

          const maxDim = Math.max(size.x, size.y, size.z);
          const fov = camera.fov * (Math.PI / 180);
          let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
          cameraZ *= 1.5;

          camera.position.set(0, maxDim * 0.3, cameraZ);
          controls.target.set(0, 0, 0);
          controls.update();
        }

        scene.add(modelGroup);

        // ─── Painter App ─────────────────────────────────────
        const cursorNode = new CursorNode(camera, renderer);
        renderer.domElement.style.cursor = "crosshair";

        painterApp = new PainterApp({
          THREE,
          GLTFLoader,
          GLTFExporter,
          scene,
          camera,
          controls,
          renderer,
          gui,
          cursorNode,
        });

        painterApp.ScenePainter(modelGroup);

        // ─── Resize Handler ──────────────────────────────────
        resizeHandler = () => {
          const container = mountRef.current;
          if (!container || !renderer || !camera) return;

          const w = container.clientWidth || 1;
          const h = container.clientHeight || 1;

          renderer.setSize(w, h);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
        };

        window.addEventListener("resize", resizeHandler);
        resizeHandler();

        // ─── Animation Loop ──────────────────────────────────
        const animate = (time) => {
          if (painterApp) painterApp.render?.(time);
          if (controls) controls.update();
          if (renderer && scene && camera) renderer.render(scene, camera);
        };

        renderer.setAnimationLoop(animate);
      } catch (err) {
        console.error("Failed to initialize scene:", err);

        const errorDiv = document.createElement("div");
        errorDiv.textContent = `Error: ${err.message}`;
        errorDiv.style.position = "absolute";
        errorDiv.style.top = "20px";
        errorDiv.style.left = "20px";
        errorDiv.style.color = "white";
        errorDiv.style.background = "rgba(200,0,0,0.8)";
        errorDiv.style.padding = "12px 16px";
        errorDiv.style.borderRadius = "6px";
        errorDiv.style.zIndex = "100";
        errorDiv.style.fontWeight = "bold";
        errorDiv.style.maxWidth = "80%";
        mountRef.current?.appendChild(errorDiv);
      }
    };

    init();

    // ─── Cleanup ─────────────────────────────────────────
    return () => {
      if (gui) gui.destroy();

      if (renderer) {
        renderer.setAnimationLoop(null);
        renderer.dispose();
      }

      if (mountRef.current) {
        while (mountRef.current.firstChild) {
          mountRef.current.removeChild(mountRef.current.firstChild);
        }
      }

      if (resizeHandler) {
        window.removeEventListener("resize", resizeHandler);
      }

      sceneRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;
    };
  }, [open, modelUrl, extension, fileName, lightParam]);

  const applyZoom = (factor) => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;

    const dir = camera.position.clone().sub(controls.target);
    dir.multiplyScalar(factor);
    camera.position.copy(controls.target.clone().add(dir));
    camera.updateProjectionMatrix();
  };

  const normalizedExt = (extension || "").toLowerCase().replace(/^\./, "");
  const isBimFormat = ["ifc", "rvt", "dwg", "bim"].includes(normalizedExt);
  const canOpenSource = typeof modelUrl === "string" && !!modelUrl;
  const bimViewerUrl = canOpenSource && isBimFormat ? `https://3dviewer.net/#model=${encodeURIComponent(modelUrl)}` : "";

  const handleSaveAsNewVersion = async () => {
    if (!fileMeta?._id || !sceneRef.current) {
      setSaveError("Missing file metadata for version save.");
      return;
    }

    try {
      setSaveLoading(true);
      setSaveError("");
      setSaveSuccess("");

      const exporter = new GLTFExporter();
      const arrayBuffer = await new Promise((resolve, reject) => {
        exporter.parse(
          sceneRef.current,
          (result) => {
            if (result instanceof ArrayBuffer) {
              resolve(result);
            } else {
              reject(new Error("GLB export returned non-binary payload."));
            }
          },
          reject,
          { binary: true }
        );
      });

      const glbBlob = new Blob([arrayBuffer], { type: "model/gltf-binary" });
      const exportFile = new File([glbBlob], `${Date.now()}-painted.glb`, {
        type: "model/gltf-binary",
      });

      const formData = new FormData();
      formData.append("fileId", fileMeta._id);
      formData.append("description", "Painted 3D model update");
      formData.append("file", exportFile);
      await editFile(formData);
      setSaveSuccess("New version saved successfully.");
    } catch (error) {
      console.error("Save version failed:", error);
      setSaveError(error?.response?.data?.error || error?.message || "Failed to save new version.");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-60">
        <div
          className={`fixed inset-0 ${
            theme === "dark"
              ? "bg-black bg-opacity-80"
              : "bg-white bg-opacity-80"
          }`}
          aria-hidden="true"
        />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            className={`w-full max-w-6xl rounded-lg overflow-hidden shadow-2xl relative ${
              theme === "dark"
                ? "bg-gray-900 text-white"
                : "bg-white text-black"
            }`}
          >
            <div ref={mountRef} className="w-full h-[600px] relative" />
            <div className="absolute left-4 top-4 z-20 flex gap-2">
              <button
                onClick={() => applyZoom(0.8)}
                className={`rounded px-3 py-2 text-sm font-semibold ${
                  theme === "dark" ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"
                }`}
                type="button"
              >
                Zoom In
              </button>
              <button
                onClick={() => applyZoom(1.25)}
                className={`rounded px-3 py-2 text-sm font-semibold ${
                  theme === "dark" ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"
                }`}
                type="button"
              >
                Zoom Out
              </button>
              <button
                onClick={handleSaveAsNewVersion}
                disabled={saveLoading}
                className={`rounded px-3 py-2 text-sm font-semibold text-white ${
                  saveLoading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
                }`}
                type="button"
              >
                {saveLoading ? "Saving..." : "Save New Version"}
              </button>
              {canOpenSource ? (
                <a
                  href={modelUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`rounded px-3 py-2 text-sm font-semibold ${
                    theme === "dark" ? "bg-indigo-700 hover:bg-indigo-600 text-white" : "bg-indigo-100 hover:bg-indigo-200 text-indigo-900"
                  }`}
                >
                  Open File
                </a>
              ) : null}
              {bimViewerUrl ? (
                <a
                  href={bimViewerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`rounded px-3 py-2 text-sm font-semibold ${
                    theme === "dark" ? "bg-emerald-700 hover:bg-emerald-600 text-white" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-900"
                  }`}
                >
                  Open BIM Viewer
                </a>
              ) : null}
            </div>
            {saveError ? (
              <div className="absolute left-4 top-16 z-20 rounded bg-red-700/90 px-3 py-2 text-xs text-white">
                {saveError}
              </div>
            ) : null}
            {saveSuccess ? (
              <div className="absolute left-4 top-16 z-20 rounded bg-green-700/90 px-3 py-2 text-xs text-white">
                {saveSuccess}
              </div>
            ) : null}
            {viewerNotice ? (
              <div className="absolute left-4 top-24 z-20 rounded bg-amber-700/90 px-3 py-2 text-xs text-white max-w-[500px]">
                {viewerNotice}
              </div>
            ) : null}

            <button
              onClick={onClose}
              className={`absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full font-bold text-xl transition-all z-10 ${
                theme === "dark"
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-black"
              }`}
              aria-label="Close"
            >
              ×
            </button>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ThreeScene;
